import { useMemo, useState } from "react";
import { Field, Panel, SelectField } from "../../components/ui";
import { fmtAmount } from "../../lib/format";
import { simulate } from "./simulate";

/** "1 000 000,5" → 1000000.5 ; empty / invalid → 0. */
const parse = (v: string) => {
  const n = Number(v.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export function SimulatorPage() {
  const [initial, setInitial] = useState("1000000");
  const [deposit, setDeposit] = useState("50000");
  const [rate, setRate] = useState("8");
  const [duration, setDuration] = useState("10");
  const [unit, setUnit] = useState<"years" | "months">("years");

  const months = Math.min(Math.round(parse(duration) * (unit === "years" ? 12 : 1)), 1200);
  const result = useMemo(
    () =>
      simulate({
        initial: parse(initial),
        monthlyDeposit: parse(deposit),
        annualRate: parse(rate),
        months,
      }),
    [initial, deposit, rate, months],
  );

  return (
    <Panel title="Simulateur d'épargne">
      <div className="inline-form">
        <Field
          label="Montant initial (FCFA)"
          inputMode="decimal"
          value={initial}
          onChange={(e) => setInitial(e.target.value)}
        />
        <Field
          label="Dépôt mensuel (FCFA)"
          inputMode="decimal"
          value={deposit}
          onChange={(e) => setDeposit(e.target.value)}
        />
        <Field
          label="Taux annuel (%)"
          inputMode="decimal"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
        />
        <Field
          label="Durée"
          inputMode="numeric"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
        />
        <SelectField
          label="Unité"
          value={unit}
          onChange={(e) => setUnit(e.target.value as "years" | "months")}
        >
          <option value="years">années</option>
          <option value="months">mois</option>
        </SelectField>
      </div>

      <div className="performance-row">
        <div className="stat-tile">
          <span className="stat-label">Capital final</span>
          <span className="stat-value">{fmtAmount(result.finalBalance)} FCFA</span>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Total versé</span>
          <span className="stat-value">{fmtAmount(result.totalContributed)} FCFA</span>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Intérêts gagnés</span>
          <span className="stat-value pnl-positive">{fmtAmount(result.totalInterest)} FCFA</span>
        </div>
      </div>
      <p className="muted">
        Intérêts composés chaque mois (taux annuel ÷ 12), dépôt ajouté en fin de mois. Simulation
        indicative : ne tient compte ni des frais ni des impôts.
      </p>

      {result.rows.length > 0 && (
        <>
          <h3 className="panel-subhead">Évolution</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Période</th>
                  <th>Total versé</th>
                  <th>Intérêts cumulés</th>
                  <th>Capital</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r) => (
                  <tr key={r.month}>
                    <td>
                      {r.month % 12 === 0 ? `Année ${r.month / 12}` : `${r.month} mois`}
                    </td>
                    <td>{fmtAmount(r.contributed)}</td>
                    <td>{fmtAmount(r.interest)}</td>
                    <td>
                      <strong>{fmtAmount(r.balance)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Panel>
  );
}
